import React, { useState, useEffect } from 'react';
import { GiChurch } from 'react-icons/gi';
import {
  LuMapPin,
  LuGlobe,
  LuMail,
  LuGraduationCap,
  LuUser,
  LuPhone,
  LuLoader,
  LuExternalLink
} from 'react-icons/lu';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../shared/firebase';
import hmCollegeImg from '../../assets/hm_college.png';
import mullarCollegeImg from '../../assets/mullar_college.png';
import malabarCollegeImg from '../../assets/malabar_college.png';

export default function Colleges({ activeSection, onSelectSection }) {
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function fetchColleges() {
      try {
        setLoading(true);
        let querySnapshot;
        try {
          querySnapshot = await getDocs(collection(db, 'college'));
          if (querySnapshot.empty) {
            querySnapshot = await getDocs(collection(db, 'colleges'));
          }
        } catch (e) {
          querySnapshot = await getDocs(collection(db, 'colleges'));
        }

        const fetched = [];
        const fallbackImgs = [mullarCollegeImg, hmCollegeImg, malabarCollegeImg];
        let idx = 0;

        if (querySnapshot && !querySnapshot.empty) {
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            const collegeName = data.collegename || data.collagename || data.name || data.title || 'CSI College';
            const order = typeof data.order === 'number' ? data.order : 999;
            
            let defaultImg = fallbackImgs[idx % fallbackImgs.length];
            const nameUpper = collegeName.toUpperCase();
            if (nameUpper.includes('HERMANN') || nameUpper.includes('PARAPPERI')) {
              defaultImg = hmCollegeImg;
            } else if (nameUpper.includes('MULLAR') || nameUpper.includes('CHOMBALA')) {
              defaultImg = mullarCollegeImg;
            } else if (nameUpper.includes('MALABAR CHRISTIAN')) {
              defaultImg = malabarCollegeImg;
            }

            const imgUrl = data.img_url || data.image || data.img || defaultImg;

            fetched.push({
              id: doc.id,
              name: collegeName,
              manager: data.manager || null,
              principal: data.principal || null,
              adminOfficer: data.administrativeofficer || data.adminOfficer || null,
              address: data.address || '',
              phone: data.phone || null,
              email: data.email || null,
              website: data.website || null,
              order,
              image: imgUrl,
              raw: data
            });
            idx++;
          });

          fetched.sort((a, b) => a.order - b.order);
        }

        if (isMounted) {
          setColleges(fetched);
        }
      } catch (err) {
        console.error("Error fetching colleges from Firebase:", err);
        if (isMounted) {
          setColleges([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchColleges();
    return () => { isMounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#5F5A58] font-sans flex flex-col pb-16">
      
      {/* Page Header */}
      <section className="relative bg-gradient-to-b from-[#FAF6F2] via-[#F5EFEE] to-[#E8DDD7] pt-28 sm:pt-32 pb-16 overflow-hidden border-b border-[#E8DDD7]">
        <div className="absolute right-[5%] top-1/2 -translate-y-1/2 pointer-events-none opacity-[0.03] select-none">
          <GiChurch className="w-64 h-64 sm:w-96 sm:h-96 text-[#7B1E3A]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-12 flex flex-col items-center md:items-start text-center md:text-left">
          <div className="w-16 h-1 bg-[#D4A64A] mb-6 rounded-full" />
          <h1 className="font-serif font-bold text-[#7B1E3A] text-[38px] sm:text-[50px] lg:text-[56px] uppercase tracking-[0.03em] leading-[1.05]">
            Colleges
          </h1>
          <p className="mt-4 max-w-xl text-[#7B6B66] text-[16px] sm:text-[17px] leading-[1.6]">
            Premier arts, science, and professional degree colleges nurturing minds, building character, and shaping futures across the Malabar region.
          </p>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="max-w-[1440px] mx-auto px-6 sm:px-8 mt-12 w-full">
        
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[24px] border border-gray-100 shadow-sm">
            <LuLoader className="w-10 h-10 text-[#842343] animate-spin mb-3" />
            <p className="text-sm font-semibold text-[#5A4F4A]">Connecting to Firebase & loading colleges...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
            {colleges.map((college, idx) => {
              const cleanWebsite = college.website ? college.website.replace(/\s+/g, '') : null;
              const formattedWebsite = cleanWebsite
                ? (cleanWebsite.startsWith('http') ? cleanWebsite : `https://${cleanWebsite}`)
                : null;

              return (
                <div
                  key={idx}
                  className="bg-white rounded-[28px] overflow-hidden border border-[#E8DDD7] shadow-sm hover:shadow-[0_20px_50px_rgba(123,30,58,0.12)] transition-all duration-300 flex flex-col justify-between group hover:-translate-y-1 h-full"
                >
                  <div>
                    {/* Image Banner */}
                    <div className="relative h-56 overflow-hidden bg-[#FAF6F2]">
                      <img 
                        src={college.image} 
                        alt={college.name} 
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = mullarCollegeImg;
                        }}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#7B1E3A]/85 via-[#7B1E3A]/25 to-transparent"></div>
                      
                      {/* College Icon Badge */}
                      <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-md">
                        <LuGraduationCap className="w-5 h-5 text-[#7B1E3A]" />
                      </div>
                    </div>
                    
                    {/* Card Content Body */}
                    <div className="p-6 sm:p-7 flex flex-col">
                      <h2 className="font-serif font-bold text-[#7B1E3A] text-lg sm:text-xl leading-snug mb-4 min-h-[56px] flex items-start">
                        {college.name}
                      </h2>
                      
                      <div className="space-y-3 mb-4">
                        {/* Manager */}
                        {college.manager && (
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#FAF6F2] flex items-center justify-center shrink-0 mt-0.5 border border-[#E8DDD7]">
                              <GiChurch className="w-4 h-4 text-[#D4A64A]" />
                            </div>
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-[#8A7E79]">Manager</p>
                              <p className="text-[#1a1715] font-semibold text-[13.5px]">{college.manager}</p>
                            </div>
                          </div>
                        )}

                        {/* Principal */}
                        {college.principal && (
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#FAF6F2] flex items-center justify-center shrink-0 mt-0.5 border border-[#E8DDD7]">
                              <LuUser className="w-4 h-4 text-[#D4A64A]" />
                            </div>
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-[#8A7E79]">Principal</p>
                              <p className="text-[#1a1715] font-semibold text-[13.5px]">{college.principal}</p>
                            </div>
                          </div>
                        )}

                        {/* Administrative Officer */}
                        {college.adminOfficer && (
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#FAF6F2] flex items-center justify-center shrink-0 mt-0.5 border border-[#E8DDD7]">
                              <LuUser className="w-4 h-4 text-[#7B1E3A]" />
                            </div>
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-[#8A7E79]">Administrative Officer</p>
                              <p className="text-[#1a1715] font-semibold text-[13.5px]">{college.adminOfficer}</p>
                            </div>
                          </div>
                        )}
                        
                        {/* Address */}
                        {college.address && (
                          <div className="flex items-start gap-3 pt-1">
                            <LuMapPin className="w-4 h-4 text-[#842343] shrink-0 mt-0.5" />
                            <p className="text-[#5F5A58] text-[13px] leading-relaxed">{college.address}</p>
                          </div>
                        )}

                        {/* Phone */}
                        {college.phone && (
                          <div className="flex items-center gap-3 pt-1">
                            <LuPhone className="w-4 h-4 text-[#842343] shrink-0" />
                            <a href={`tel:${college.phone}`} className="text-[#1a1715] hover:text-[#842343] font-semibold text-[13px] transition-colors">
                              {college.phone}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Bottom Action Links */}
                  <div className="p-6 sm:p-7 pt-0">
                    <div className="flex flex-col gap-2 pt-4 border-t border-[#F0EBE6]">
                      {formattedWebsite && (
                        <a
                          href={formattedWebsite}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-xs font-bold text-[#842343] hover:text-[#6c1b35] transition-colors"
                        >
                          <LuGlobe className="w-3.5 h-3.5" />
                          <span className="truncate">{cleanWebsite.replace(/^https?:\/\//, '')}</span>
                          <LuExternalLink className="w-3 h-3 ml-auto" />
                        </a>
                      )}
                      
                      {college.email && (
                        <a
                          href={college.email.includes('@') ? `mailto:${college.email}` : college.email}
                          target={college.email.includes('@') ? '_self' : '_blank'}
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-xs font-semibold text-[#5F5A58] hover:text-[#842343] transition-colors truncate"
                        >
                          <LuMail className="w-3.5 h-3.5 shrink-0 text-[#842343]" />
                          <span className="truncate">{college.email}</span>
                        </a>
                      )}
                    </div>
                  </div>

                </div>
              );
            })}
          </div>
        )}

      </section>
    </div>
  );
}
